<template>
  <BaseInput
    class="PostalCodeComponent"
    :id="id"
    :label="label"
    v-model="postalCode"
    :placeholder="placeholder"
    :rules="validations"
    icon="icon-home-01"
    :loading="isWaitingService"
    :serviceValidationError="serviceValidation.error"
    :serviceValidationMessage="serviceValidation.message"
    :editable="editable"
    :maxlength="5"
    @paste="(e) => e.preventDefault()"
    @keydown="isNumberKey"
    @focus="onFocus"
  />
</template>

<script setup lang="ts">
  import { ref, watch, onBeforeMount, onMounted, computed } from 'vue'
  import { isNumberKey } from '@/utils/input.utils'
  import { useI18n } from 'vue-i18n'
  import { useCommonStore } from '@/modules/core/stores/commonStore'
  import BaseInput from '@/modules/core/components/ui/BaseInput.vue'
  import { validatePostalCode } from '@/utils/validations.utils'
  import type { ValidationRuleType } from '@/modules/core/models/types'
  import { ErrorDisplayTypeEnum } from '../../models/enums'

  const commonStore = useCommonStore()
  const { t } = useI18n()

  interface PropsInterface {
    id: string
    modelValue: string | null
    placeholder?: string
    rules?: ValidationRuleType[]
    label?: string
    icon?: string
    editable?: boolean
  }
  const props = withDefaults(defineProps<PropsInterface>(), {
    modelValue: '',
    rules: () => [],
    placeholder: '',
    editable: true
  })

  const emit = defineEmits(['update:modelValue', 'focus', 'service-loading', 'enter-focus'])

  const validationRules = {
    validPostalCode: (value: string) =>
      (validatePostalCode(value) && !serviceValidation.value.error) ||
      t('common.validations.postal-code')
  }
  const validations = [...props.rules, validationRules.validPostalCode]
  const isRenderedInModal = ref(false)
  const isWaitingService = ref(false)
  const serviceValidation = ref({ error: false, message: '' })
  const wrongPostalCode = ref('') // Para enseñar el codigo postal cuando es erroneo apesar de emit un null hace arriba

  const postalCode = computed({
    get: () => props.modelValue ?? wrongPostalCode.value ?? '',
    set: (value: string) => {
      if (value !== '' && value === wrongPostalCode.value) return

      emit('update:modelValue', value)
    }
  })

  watch(
    () => isWaitingService.value,
    (isWaiting: boolean) => {
      if (isWaiting) return

      const afterServiceSuccessfully = !serviceValidation.value.error
      if (afterServiceSuccessfully) {
        wrongPostalCode.value = ''
        emit('update:modelValue', postalCode.value)
      } else {
        wrongPostalCode.value = postalCode.value
        emit('update:modelValue', null)
      }
    }
  )

  function onFocus(focussed: boolean) {
    if (focussed) emit('enter-focus')
    if (focussed || !postalCode.value.length) return

    if (!validatePostalCode(postalCode.value!)) return

    validatePostalCodeWithService(postalCode.value)

    emit('focus', focussed)
  }

  async function validatePostalCodeWithService(postalCode: string) {
    serviceValidation.value.error = false
    serviceValidation.value.message = ''
    isWaitingService.value = true
    //const { total } = await commonStore.findPostalCodes(postalCode)
    const errorDisplayType = isRenderedInModal.value
      ? ErrorDisplayTypeEnum.TOAST_IN_MODAL
      : ErrorDisplayTypeEnum.TOAST
    const arrCP = await commonStore.findPostalCodesAuto(postalCode, errorDisplayType)
    isWaitingService.value = false

    serviceValidation.value.error = !arrCP.length
    serviceValidation.value.message = !arrCP.length ? t('common.validations.postal-code') : ''
  }

  onBeforeMount(() => {
    // if (postalCode.value) {
    //   validatePostalCodeWithService(postalCode.value)
    // }
  })

  onMounted(() => {
    isRenderedInModal.value = !!document.querySelector('.modal-component')
  })

  watch(isWaitingService, () => {
    emit('service-loading', isWaitingService.value)
  })
</script>
